import { TranslationContext } from 'contexts/translation-context';
import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { formatFloatWithCommasAndDecimals } from 'utils/new-formats';
import { Icon } from 'components/common/icon/icon';
import Tooltip from 'components/common/tooltip';
import type { PackagePrices } from 'types/join';
import { getPlanText } from 'utils/package-helper';
import { useDispatch } from 'react-redux';
import { closeModal } from 'components/common/modal/modal-actions';
import { getPlanName } from './formats';
import { resetError } from '../actions';

interface colorProps {
  planColor: string;
}

const HorizontalWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  height: max-content;
  &:hover {
    background: #f0f0f0;
  }
`;
const TooltipWrapper = styled.div`
  display: flex;
`;
const VerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const PlanRapNetText = styled.div<colorProps>`
  color: ${props => props.planColor};
  font-size: 9px;
  font-family: ${props => props.theme.fonts.montserratBold};
  padding-top: 3px;
`;
const PlanText = styled.div<colorProps>`
  color: ${props => props.planColor};
  height: max-content;
  font-size: 15px;
  font-family: ${props => props.theme.fonts.montserratBold};
`;
const PriceText = styled.div`
  color: ${props => props.theme.colors.siteBlack};
  font-size: 12px;
  font-family: ${props => props.theme.fonts.montserratMedium};
  padding-top: 12px;
`;
const LightLine = styled.div`
  margin: 10px 0;
  border-bottom: 1px solid ${props => props.theme.colors.siteGrey};
`;

const Checkbox = styled.input.attrs({ type: 'radio' })`
  width: 18px;
  height: 18px;
  margin-top: 10px;
  margin-right: 8px;
  background-color: ${props => props.theme.colors.white};
  border-radius: 50%;
  vertical-align: middle;
  border: 2px solid #575757;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  cursor: pointer;
  position: relative;

  &:hover,
  &:checked {
    border-color: ${props => props.theme.colors.red};
    background-color: ${props => props.theme.colors.white};

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 10px;
      height: 10px;
      background-color: ${props => props.theme.colors.red};
      border-radius: 50%;
    }
  }
`;

const Header = styled.div`
  font-family: ${props => props.theme.fonts.montserratSemiBold};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 20px;
  display: flex;
  justify-content: center;
  padding-bottom: 30px;
`;
const Container = styled.div`
  width: 545px;
  padding: 40px 40px 0px 20px;
  min-height: 288px;
`;
const TooltipIcon = styled(Icon)`
  color: ${props => props.theme.colors.lightGrey};
  font-size: 9px;
  margin: 4px;
`;
const StyledTooltip = styled(Tooltip)`
  height: 9px;
`;

const UpdateButton = styled.button`
  width: 200px;
  height: 40px;
  margin: 20px auto;
  display: block;
  background-color: #4ebaf0;
  color: white;
  font-family: ${props => props.theme.fonts.montserratSemiBold};
  font-size: 14px;
  text-align: center;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  ${props =>
    !props.disabled &&
    css`
      &:hover {
        background-color: #3a9dc7;
      }
    `}
`;
export const AdditionalPlans = ({
  userAvailablePlans,
  setSelectedPlan,
  getPlanColor,
  setSelectedAddon,
  setNumOfAdditionalUser
}: {
  userAvailablePlans: (PackagePrices | undefined)[];
  setSelectedPlan: (data: PackagePrices) => void;
  getPlanColor: (plan?: string) => string;
  setSelectedAddon: (data: PackagePrices[]) => void;
  setNumOfAdditionalUser: (number: string) => void;
}) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [chosenPlan, setChosenPlan] = useState<PackagePrices | undefined>(undefined);
  const dispatch = useDispatch();

  const onSelectPlan = (plan: PackagePrices | undefined) => {
    setChosenPlan(plan);
  };

  const updatePlan = () => {
    if (chosenPlan === undefined) return;
    setSelectedPlan(chosenPlan);
    setSelectedAddon([]);
    setNumOfAdditionalUser('1');
    dispatch(closeModal());
    dispatch(resetError());
  };

  return (
    <Container>
      <Header>{TRANSLATION_TEXT.other_available_plans}</Header>
      {userAvailablePlans.map((plan, i, { length }) => (
        <div
          key={plan?.packageName}
          onClick={() => onSelectPlan(plan)}
          style={{ cursor: 'pointer' }}
        >
          <HorizontalWrapper>
            <TooltipWrapper>
              <Checkbox
                checked={chosenPlan?.packageName === plan?.packageName}
                onClick={() => onSelectPlan(plan)}
              />
              <VerticalWrapper>
                <TooltipWrapper>
                  <PlanRapNetText planColor={getPlanColor(plan?.packageName)}>
                    {getPlanName(plan?.packageName)
                      .split(' ')[0]
                      .toUpperCase()}
                  </PlanRapNetText>
                  <StyledTooltip
                    black
                    tooltip={TRANSLATION_TEXT[getPlanText(plan?.packageName ?? '') || 'empty_text']}
                  >
                    <TooltipIcon name="info" />
                  </StyledTooltip>
                </TooltipWrapper>
                <PlanText planColor={getPlanColor(plan?.packageName)}>
                  {getPlanName(plan?.packageName)
                    .split(' ')
                    .map((p, index) => index !== 0 && `${p.toUpperCase()} `)}
                </PlanText>
              </VerticalWrapper>
            </TooltipWrapper>
            <PriceText>${formatFloatWithCommasAndDecimals(plan?.price || 0)}</PriceText>
          </HorizontalWrapper>
          {i + 1 !== length && <LightLine />}
        </div>
      ))}
      <UpdateButton disabled={!chosenPlan} onClick={() => updatePlan()}>
        {TRANSLATION_TEXT.update_my_plan}
      </UpdateButton>
    </Container>
  );
};
